import { useMemo, useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import messages from "messages";
import { Link } from "react-router-dom";
// components
import ConfirmModal from "components/confirm-modal";
import { ReactComponent as OutlineEdit1 } from "assets/svg/outline-edit-1.svg";
import { ReactComponent as OutlineEyeOpen } from "assets/svg/outline-eye-open.svg";
import { ReactComponent as OutlineMenuVertical } from "assets/svg/outline-menu-vertical.svg";
import { ReactComponent as OutlineDownload } from "assets/svg/outline-download.svg";
import { ReactComponent as OutlineTrash1 } from "assets/svg/outline-trash-1.svg";
import { ReactComponent as OutlineFolderMoveTo } from "assets/svg/outline-folder-move-to.svg";
import { ReactComponent as OutlineArrowRight } from "assets/svg/outline-arrow-right.svg";
import IconButton from "components/icon-button";
// styles
import styles from "./ActionsMenu.module.scss";
// hooks
import { useActionsMenu } from "./contexts/actions-menu-context/ActionsMenuContext";
import { useTreeWidgetContext } from "../tree-widget/contexts/TreeWidgetContext";
import { useNoteByAttachmentIdContext } from "components/Dashboard/DoctorDashboard/patient-charts/components/patient-chart/components/attachments-tab/contexts/NoteByAttachmentIdContext";
// types
import type {
  OrchidDriveDirectory,
  OrchidDriveFile,
} from "contexts/orchid-drive/types";
// import { downloadFile } from "contexts/orchid-drive-download";
import type { AttachmentsViewProps } from "../..";
import { AmplitudePageName, track } from "utils/amplitude";

type Props = {
  downloadHref?: string;
  viewHref?: string;
  storageDrive?: StorageDrive;
  entryId: string;
  type: string | undefined;
  entry: OrchidDriveDirectory | OrchidDriveFile;
  onRename: (arg: string) => void;
  onDownload?: () => void;
  analyticsPageName: AmplitudePageName;
} & Pick<AttachmentsViewProps, "deleteEntry">;

const ActionsMenu = ({
  downloadHref,
  viewHref,
  deleteEntry,
  entryId,
  type,
  entry,
  onRename,
  onDownload,
  storageDrive,
  analyticsPageName,
}: Props) => {
  const { reset, register, unregister, registered } = useActionsMenu();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    return () => unregister(entryId);
  }, [entryId, unregister]);

  // TODO: can be refactored based on storageDrive type to be cleaner
  const { open } = useTreeWidgetContext();
  const { noteByAttachmentId } = useNoteByAttachmentIdContext();

  const note = useMemo(
    () => noteByAttachmentId[entryId],
    [entryId, noteByAttachmentId],
  );

  return (
    <>
      {/* confirm delete entry */}
      <ConfirmModal
        labelTitle={messages.areYouSure}
        children={`Do you want to delete this ${
          type === "folder" ? "folder" : "file"
        }?`}
        labelConfirm={messages.delete}
        hideCancel={true}
        show={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={() =>
          deleteEntry &&
          deleteEntry({
            entryId: entryId,
          })
        }
      />
      <Dropdown
        className={styles["actions-dropdown"]}
        onMouseDown={(e) => e.stopPropagation()}
        onToggle={(isOpen) => {
          if (isOpen) {
            reset();
            register(entryId);
          } else {
            unregister(entryId);
          }
        }}
        show={registered.includes(entryId)}
      >
        <Dropdown.Toggle
          bsPrefix="p-0"
          as={IconButton}
          className={styles["actions-dropdown__toggle"]}
        >
          <OutlineMenuVertical />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {/* preview */}
          {type !== "folder" && (
            <Dropdown.Item
              draggable={false}
              className={styles["actions-dropdown__action-item"]}
              href={viewHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                track(analyticsPageName, "preview-dropdown-item", "click")
              }
            >
              <OutlineEyeOpen className="mr-2" />
              {messages.preview}
            </Dropdown.Item>
          )}
          {/* rename, not for note attachments */}
          {storageDrive !== "note-attachment" && note === undefined && (
            <Dropdown.Item
              draggable={false}
              className={styles["actions-dropdown__action-item"]}
              onClick={() => {
                track(analyticsPageName, "rename-dropdown-item", "click");
                onRename(entryId);
              }}
            >
              <OutlineEdit1 className="mr-2" />
              {messages.rename}
            </Dropdown.Item>
          )}
          {/* go to note, only for note attachments */}
          {note !== undefined && (
            <Dropdown.Item
              draggable={false}
              as={Link as any}
              target="_blank"
              rel="noopener noreferrer"
              // only signed note attachment will render in attachments tab
              to={`/pro-dashboard/notes-and-forms/note/${note.note_data_id}?patient_chart_tab_redirect=attachments`}
              className={styles["actions-dropdown__action-item"]}
              disabled={type === "folder"}
              onClick={() =>
                track(analyticsPageName, "go-to-note-dropdown-item", "click")
              }
            >
              <OutlineArrowRight className="mr-2" />
              {messages.goToNote}
            </Dropdown.Item>
          )}
          {/* download */}
          <Dropdown.Item
            draggable={false}
            href={type === "folder" ? undefined : downloadHref}
            className={styles["actions-dropdown__action-item"]}
            onClick={() => {
              track(analyticsPageName, "download-dropdown-item", "click");
              type === "folder" && onDownload && onDownload();
            }}
          >
            <OutlineDownload className="mr-2" />
            {messages.download}
          </Dropdown.Item>
          {open && (
            <Dropdown.Item
              draggable={false}
              onClick={() => {
                track(analyticsPageName, "move-to-dropdown-item", "click");
                open &&
                  open([
                    {
                      guid: entryId,
                      type,
                      ...entry,
                    },
                  ]);
              }}
              className={styles["actions-dropdown__action-item"]}
            >
              <OutlineFolderMoveTo className="mr-2" />
              {messages.moveTo}
            </Dropdown.Item>
          )}
          {deleteEntry && (
            <Dropdown.Item
              draggable={false}
              disabled={!deleteEntry}
              onClick={() => {
                track(analyticsPageName, "delete-file-dropdown-item", "click");
                setShowConfirmModal(true);
              }}
              className={styles["actions-dropdown__action-item"]}
            >
              <OutlineTrash1 className="mr-2" />
              {messages.delete}
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
};

export default ActionsMenu;
